import React, { ComponentPropsWithoutRef } from 'react';
import { makeStyles, InlineCode } from '@rbx/ui';
import DynamicallyRouted from '@modules/miscellaneous/common/components/DynamicallyRouted';
import { getCustomInlineCodeFontSize } from '@modules/layout/layout/constants/layoutConstants';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import resolveLinkFromInlineCodeText from '../utils/inlineCodeLinkResolver';
import { useMarkdown } from './MarkdownProvider';

const useInlineCodeStyles = (
  enableEngineReferenceRevamp: boolean,
  whiteSpace?: string,
  customStyle?: React.CSSProperties | Record<string, unknown>,
  customLinkStyle?: React.CSSProperties | Record<string, unknown>,
) =>
  makeStyles()(() => ({
    inlineCode: {
      whiteSpace: whiteSpace ?? 'auto-phrase',
      ...customStyle,
      fontSize: getCustomInlineCodeFontSize(enableEngineReferenceRevamp),
    },
    inlineCodeWithLink: {
      whiteSpace: whiteSpace ?? 'auto-phrase',
      ...customLinkStyle,
      fontSize: getCustomInlineCodeFontSize(enableEngineReferenceRevamp),
    },
  }));

export type MarkdownInlineCodeProps = ComponentPropsWithoutRef<typeof InlineCode> & {
  whiteSpace?: 'normal' | 'break-all' | 'keep-all' | 'break-word' | 'auto-phrase';
  inlineCodeStyle?: React.CSSProperties | Record<string, unknown>;
  inlineCodeLinkStyle?: React.CSSProperties | Record<string, unknown>;
};

export default function MarkdownInlineCode({
  whiteSpace,
  inlineCodeStyle,
  inlineCodeLinkStyle,
  children,
}: MarkdownInlineCodeProps) {
  const { enableEngineReferenceRevamp } = useAppConfig();
  const { link, text } = resolveLinkFromInlineCodeText(children?.toString());
  const {
    classes: { inlineCode, inlineCodeWithLink },
    cx,
  } = useInlineCodeStyles(
    !!enableEngineReferenceRevamp,
    whiteSpace,
    inlineCodeStyle,
    inlineCodeLinkStyle,
  )();
  const { classes } = useMarkdown();
  return (
    <InlineCode
      classes={{
        root: cx(
          'inline-code',
          link !== null ? inlineCodeWithLink : inlineCode,
          classes?.inlineCode,
        ),
      }}>
      {link === null ? (
        text
      ) : (
        <DynamicallyRouted.Link
          href={`${link}`}
          underline='none'
          color='primary'
          style={
            inlineCodeLinkStyle ? { fontWeight: 400 } : undefined
          }>{`${text}`}</DynamicallyRouted.Link>
      )}
    </InlineCode>
  );
}
